import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import styles from './edit-blacklist-entry-modal.module.less';
import { messages } from './messages';
import ModalPortal from '../../../../../../components/modal-portal';
import { formatAddress } from '../../../../../products-page/purchase-orders/address-helpers';
import { updateBlacklistEntrySchema } from '../../../../../../utils/blacklist-address-schema';
import { useYupFormValidation } from '../../../../../../validation/use-yup-form-validation';

/** @typedef {import('../../types').BlacklistEntry} BlacklistEntry */
/** @typedef {import('../../types').UpdateBlacklistEntryPayload} UpdateBlacklistEntryPayload */

/**
 * @param {object} props
 * @param {BlacklistEntry} props.entry
 * @param {() => void} props.onClose
 * @param {(entryId: number, payload: UpdateBlacklistEntryPayload) => void} props.onSave
 * @param {boolean} [props.isEditing]
 * @returns {JSX.Element}
 */
export const EditBlacklistEntryModal = ({ entry, onClose, onSave, isEditing = false }) => {
  const intl = useIntl();
  const [reason, setReason] = useState(entry.reason ?? '');

  // Include all required fields from entry for schema validation
  const form = useMemo(
    () => ({
      line1: entry.addressLine1,
      line2: entry.addressLine2,
      city: entry.city,
      state: entry.state,
      postalCode: entry.postalCode,
      countryCode: entry.countryCode,
      reason,
    }),
    [entry, reason],
  );
  const { errors, isValid } = useYupFormValidation({ form, schema: updateBlacklistEntrySchema });

  const reasonError = errors.reason?.message;

  const hasChanges = useMemo(() => {
    const originalReason = entry.reason ?? '';
    return reason !== originalReason;
  }, [reason, entry.reason]);

  const handleReasonChange = useCallback((event) => {
    setReason(event.target.value);
  }, []);

  const handleSave = useCallback(() => {
    /** @type {UpdateBlacklistEntryPayload} */
    const payload = {
      addressLine1: entry.addressLine1,
      addressLine2: entry.addressLine2,
      city: entry.city,
      state: entry.state,
      postalCode: entry.postalCode,
      countryCode: entry.countryCode,
      reason: reason.trim() || null,
    };
    onSave(entry.id, payload);
  }, [entry, reason, onSave]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <Text textType={TextType.H3} value={messages.header} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          <FlexColumn gap={Gap.Space16}>
            <Text className={styles.description} textType={TextType.Description} value={messages.description} />

            <div className={styles['address-section']}>
              <Text className={styles['address-label']} textType={TextType.Label} value={messages.addressLabel} />
              <div className={styles['address-display']}>{formatAddress(entry)}</div>
            </div>

            {entry.referenceOrderId ? (
              <div className={styles['reference-order-section']}>
                <Text className={styles['reference-order-label']} textType={TextType.Label} value={messages.referenceOrderIdLabel} />
                <Text textType={TextType.Paragraph} value={entry.referenceOrderId.toString()} />
              </div>
            ) : null}

            <Input
              hasError={Boolean(reasonError)}
              inlineMessage={reasonError}
              inlineMessageType={reasonError ? INLINE_MESSAGE_TYPES.error : undefined}
              label={intl.formatMessage(messages.reasonLabel)}
              name='reason'
              onChange={handleReasonChange}
              placeholder={intl.formatMessage(messages.reasonPlaceholder)}
              value={reason}
              version={version2}
            />
          </FlexColumn>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton disabled={isEditing} />
          <Button disabled={isEditing || !hasChanges || !isValid} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
            <Text textType={TextType.Label} value={isEditing ? messages.saving : messages.save} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
